import { useState, useEffect, useCallback } from "react";

const TRACKED_ANALYSIS_URLS_KEY = "tcbase_active_analysis_list";

const readTrackedAnalysisUrlsFromStorage = (): string[] => {
  const stored = localStorage.getItem(TRACKED_ANALYSIS_URLS_KEY);
  return stored ? JSON.parse(stored) : [];
};

/**
 * Hook that manages a synchronized list of analysis URLs being tracked.
 * All open tabs share the same list from localStorage.
 */
const useActiveUrlAnalyses = () => {
  const [trackedAnalysisUrls, setTrackedAnalysisUrls] = useState<string[]>(
    readTrackedAnalysisUrlsFromStorage
  );

  useEffect(() => {
    const syncWithOtherTabs = (event: StorageEvent) => {
      if (event.key === TRACKED_ANALYSIS_URLS_KEY) {
        const updatedUrls = event.newValue ? JSON.parse(event.newValue) : [];
        setTrackedAnalysisUrls(updatedUrls);
      }
    };

    window.addEventListener("storage", syncWithOtherTabs);

    return () => {
      window.removeEventListener("storage", syncWithOtherTabs);
    };
  }, []);

  const startTrackingAnalysis = useCallback((url: string) => {
    const currentUrls = readTrackedAnalysisUrlsFromStorage();

    if (!currentUrls.includes(url)) {
      const updatedUrls = [...currentUrls, url];

      localStorage.setItem(
        TRACKED_ANALYSIS_URLS_KEY,
        JSON.stringify(updatedUrls)
      );

      setTrackedAnalysisUrls(updatedUrls);
    }
  }, []);

  const stopTrackingAnalysis = useCallback((url: string) => {
    const currentUrls = readTrackedAnalysisUrlsFromStorage();
    const updatedUrls = currentUrls.filter(
      (trackedUrl: string) => trackedUrl !== url
    );

    localStorage.setItem(
      TRACKED_ANALYSIS_URLS_KEY,
      JSON.stringify(updatedUrls)
    );

    if (updatedUrls.length === 0) {
      localStorage.removeItem(TRACKED_ANALYSIS_URLS_KEY);
    }

    setTrackedAnalysisUrls(updatedUrls);
  }, []);

  return {
    activeAnalyses: trackedAnalysisUrls,
    addAnalysis: startTrackingAnalysis,
    removeAnalysis: stopTrackingAnalysis
  };
};

export default useActiveUrlAnalyses;
